/* eslint-disable react-perf/jsx-no-new-function-as-prop */
/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import {type Dispatch, type FC, type SetStateAction, useCallback} from 'react';
import {Dialog, DialogPanel, DialogTitle} from '@headlessui/react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import NextImage from 'next/image';

import {ArrowLeftIcon} from '@/modules/foundation/components/icons/arrow-left-icon';
import {ArrowRightIcon} from '@/modules/foundation/components/icons/arrow-right-icon';
import {FullScreenExitIcon} from '@/modules/foundation/components/icons/full-screen-exit-icon';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import {useDotButton} from './hooks/useDotButton';
import {usePrevNextButtons} from './hooks/usePrevNextButtons';

interface Props {
	startIndex: number;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	isOpen: boolean;
	images: KeyedArray<ImageWeb>;
}

export const Lightbox: FC<Props> = ({startIndex, setIsOpen, isOpen, images}) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: images.length > 1,
		startIndex: startIndex,
	});
	const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);

	const {prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick} =
		usePrevNextButtons(emblaApi);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, [setIsOpen]);

	return (
		<Dialog open={isOpen} onClose={handleClose} className={clsx('relative', 'z-50')}>
			{/* Full-screen container to center the panel */}
			<div
				className={clsx(
					'fixed',
					'inset-0',
					'flex',
					'w-screen',
					'h-screen',
					'items-center',
					'justify-center',
				)}
			>
				{/* The actual dialog panel  */}
				<DialogPanel
					className={clsx(
						'relative',
						'flex',
						'bg-black/90',
						'w-full',
						'h-full',
						'max-h-screen',
						'text-white',
						'py-[10svh]',
						'sm:px-[10vw]',
					)}
				>
					<DialogTitle className={clsx('sr-only')}>Fullskjerm bildegalleri</DialogTitle>
					<button
						onClick={handleClose}
						className={clsx(
							'opacity-70',
							'hover:opacity-100',
							'top-2',
							'absolute',
							'right-2',
						)}
						aria-label="Lukk fullskjerm bildegalleri"
					>
						<FullScreenExitIcon size={40} color="white" />
					</button>

					<div ref={emblaRef} className="flex shrink-0 grow overflow-hidden">
						<div className="flex grow touch-pan-y touch-pinch-zoom">
							{images.map((image) => (
								<div
									key={image._key}
									className="relative shrink-0 grow-0 basis-full"
								>
									<NextImage
										src={image.image.src}
										alt={image.altText ?? ''}
										className="object-contain object-center"
										sizes="90vw"
										fill
									/>
								</div>
							))}
						</div>
						{images.length > 1 && (
							<>
								<button
									type="button"
									onClick={onPrevButtonClick}
									disabled={prevBtnDisabled}
									aria-label="Forrige bilde"
									className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full text-white opacity-70 hover:opacity-100 focus:opacity-100 disabled:opacity-30"
								>
									<ArrowLeftIcon size={32} />
								</button>

								<button
									type="button"
									onClick={onNextButtonClick}
									disabled={nextBtnDisabled}
									aria-label="Neste bilde"
									className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-4 text-white opacity-70 hover:opacity-100 focus:opacity-100 disabled:opacity-30"
								>
									<ArrowRightIcon size={32} />
								</button>

								<div className="absolute inset-x-0 bottom-4 flex flex-wrap items-center justify-center gap-2">
									{scrollSnaps.map((_, index) => (
										<button
											type="button"
											key={index}
											onClick={() => onDotButtonClick(index)}
											aria-label={`Bilde ${index + 1}`}
											className={clsx(
												"relative h-1 w-10 bg-white opacity-70 before:absolute before:inset-[-8px] before:content-[''] hover:opacity-100 focus:opacity-100 disabled:opacity-30",
												selectedIndex === index ? 'opacity-100' : '',
											)}
										/>
									))}
								</div>
							</>
						)}
					</div>
				</DialogPanel>
			</div>
		</Dialog>
	);
};
